/**
 * Terms and Conditions on the customer quotation.
 *
 * Two live fields: `tc_name` (Link → `Terms and Conditions`) and `terms` (Text Editor). Choosing a
 * template fills the text; the text stays editable afterwards, because a quotation routinely needs
 * one clause changed — a validity date, a delivery charge threshold — without inventing a new
 * template for it.
 *
 * ---- The two are written separately, on purpose ----
 *
 * `tc_name` records WHICH template was chosen and `terms` is what the customer will actually read.
 * Editing the text does not change the template, and re-selecting the template replaces the text.
 * Sending both on every keystroke would make one silently overwrite the other.
 *
 * ---- Rendered by default, edited as plain text ----
 *
 * `terms` is stored as HTML because the ERP's own editor (Quill) writes it that way — that is
 * where the bold clauses, the numbered lists and the red delivery line come from. None of that is
 * ours; it is authored on the `Terms and Conditions` master and arrives with the template.
 *
 * A purchaser should never have to read or write that markup, so the card renders it and edits it
 * as plain text (Akhil, 2026-09-04). The raw-HTML mode that used to sit alongside is REMOVED at his
 * request.
 *
 * ---- What that costs, and why it is survivable ----
 *
 * A plain-text round trip flattens formatting: a template of numbered clauses saved from the text
 * box comes back as plain paragraphs. `hasRichFormatting` predicts exactly that and warns BEFORE
 * the edit rather than after the save.
 *
 * And it is recoverable — re-picking the template refetches its text and replaces the flattened
 * copy, so the loss is one click away from being undone rather than permanent. That is what makes
 * removing the HTML escape hatch acceptable.
 */

import { useEffect, useState } from 'react'
import { Card, Button } from '../../components/ui'
import { LinkCombo } from '../../components/LinkPicker'
import { useTermsTemplate, useTermsPicker } from '../../lib/api/useTerms'
import { hasRichFormatting } from '../../lib/termsHtml'
import { textToHtml } from '../../lib/rfq'
import { htmlToText } from '../../lib/mail'

export function TermsCard({
  tcName,
  terms,
  saving,
  onCommit,
}: {
  tcName: string
  terms: string
  /**
   * A write is in flight — DIMS only, never disables. `busy` here is the SHARED
   * `updateTerms.isPending`, so a commission field committing on blur would otherwise freeze
   * this terms editor mid-typing. Same trap the commission panel documents.
   */
  saving: boolean
  onCommit: (patch: { tc_name?: string; terms?: string }) => void
}) {
  const [open, setOpen] = useState(false)
  /**
   * How the terms are being shown: as the customer sees them, as plain text to edit, or as the
   * raw HTML. `read` is the default — see the header.
   */
  const [mode, setMode] = useState<'read' | 'text'>('read')
  const [draft, setDraft] = useState(terms)
  /** The plain-text draft, kept separately so switching modes cannot silently convert anything. */
  const [textDraft, setTextDraft] = useState<string | null>(null)
  useEffect(() => {
    setDraft(terms)
    setTextDraft(null)
  }, [terms])

  /* Formatting a plain-text edit would flatten — warned about BEFORE the edit, not after. */
  const wouldFlatten = hasRichFormatting(draft)

  /** Fetches a template's text so choosing one can fill the editor. */
  const template = useTermsTemplate()
  /* What would actually be saved — the converted text in text mode, the raw draft otherwise. */
  const pending = mode === 'text' && textDraft !== null ? textToHtml(textDraft) : draft
  const dirty = pending !== terms

  return (
    <Card style={{ marginBottom: 14, ...(saving ? { opacity: 0.6 } : {}) }}>
      <div
        role="button"
        // Still opens when the page is read-only — see `ReadOnlyGate`.
        data-readonly-allow=""
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen((v) => !v)
          }
        }}
        style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
      >
        <b>📜 Terms and conditions</b>
        {/* Readable while closed — terms go to the customer, and a collapsed card that says
            nothing is how a quotation goes out under the wrong ones. */}
        <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>
          {tcName ? (
            <>
              <b style={{ color: 'var(--ink)' }}>{tcName}</b>
              {terms ? '' : ' · no text'}
            </>
          ) : terms ? (
            'custom text, no template'
          ) : (
            <span style={{ color: 'var(--warn)' }}>none set</span>
          )}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ color: 'var(--muted)' }}>{open ? '▴' : '▾'}</span>
      </div>

      {open ? (
        <div style={{ marginTop: 12 }}>
          <label className="ef" style={{ maxWidth: 380 }}>
            <span>Template</span>
            <LinkCombo
              value={tcName}
              placeholder="Search terms templates…"
              useOptions={useTermsPicker}
              onChange={async (name) => {
                // Replaces the text with the template's — choosing a template is a decision to use
                // its wording, and leaving the previous text under a new name is the confusing
                // half-state this avoids.
                const text = await template.fetch(name)
                onCommit({ tc_name: name, ...(text !== null ? { terms: text } : {}) })
              }}
            />
          </label>

          <div style={{ marginTop: 10 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>Terms shown to the customer</span>
              <span style={{ flex: 1 }} />
              {/* Three ways to look at the same terms. "Edit text" is the one a purchaser wants;
                  "HTML" stays for the case where the exact markup matters. */}
              <button
                type="button"
                className="linkish"
                onClick={() => {
                  // Seeded from the CURRENT html, once — so entering text mode never rewrites
                  // anything by itself. Nothing converts back until Save.
                  if (textDraft === null) setTextDraft(htmlToText(draft))
                  setMode(mode === 'text' ? 'read' : 'text')
                }}
              >
                {mode === 'text' ? 'Preview' : 'Edit text'}
              </button>

            </div>

            {mode === 'text' ? (
              <>
                {/* Said before they type, not after they save. */}
                {wouldFlatten ? (
                  <div style={{ fontSize: 11.5, color: 'var(--warn)', marginBottom: 4 }}>
                    ⚠ These terms carry formatting — numbered clauses, bold, colour — authored in
                    the ERP's own editor. Saving from this box flattens all of it to plain
                    paragraphs. To get it back, re-pick the template above.
                  </div>
                ) : null}
                <textarea
                  value={textDraft ?? ''}
                  rows={12}
                  aria-label="Terms as plain text"
                  onChange={(e) => setTextDraft(e.target.value)}
                  style={{ width: '100%', fontFamily: 'inherit', fontSize: 12.5, lineHeight: 1.6 }}
                />
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>
                  Write it as you would in an email — a blank line starts a new paragraph. It is
                  converted to the format the quotation stores when you save.
                </div>
              </>

            ) : (
              /*
                Rendered, because this is what the customer reads. The HTML is our own — it comes
                from the `Terms and Conditions` master or from the box above, never from a supplier
                or a customer — which is what makes rendering it here reasonable.
              */
              <div
                style={{
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                  background: 'var(--panel)',
                  padding: '10px 12px',
                  minHeight: 90,
                  maxHeight: 320,
                  overflow: 'auto',
                  fontSize: 12.5,
                  lineHeight: 1.55,
                }}
              >
                {draft.trim() === '' ? (
                  <span style={{ color: 'var(--muted)' }}>
                    No terms yet — choose a template above, or add them with “Edit HTML”.
                  </span>
                ) : (
                  <div className="termshtml" dangerouslySetInnerHTML={{ __html: draft }} />
                )}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
            <span style={{ color: 'var(--muted)', fontSize: 11.5, flex: 1 }}>
              {mode === 'text'
                ? 'Plain text. Converted on save; edits stay on this quotation — the template is untouched.'
                : 'Shown as the customer will see it. Edits stay on this quotation — the template is untouched.'}
            </span>
            {dirty ? (
              <Button
                size="sm"
                onClick={() => {
                  setDraft(terms)
                  setTextDraft(null)
                }}
              >
                Discard
              </Button>
            ) : null}
            <Button size="sm" variant="primary" disabled={!dirty || saving} onClick={() => onCommit({ terms: pending })}>
              {saving ? 'Saving…' : 'Save terms'}
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  )
}
